import {Alert, Button, Table} from "antd";
import React, {FC, memo, useEffect, useState} from "react";
import {ColumnProps} from "antd/lib/table";
import moment from "moment";
import {
    CamtScheduledPaymentInput,
    GetUnpaidScheduledPaymentsQuery,
    useCreateDummyCamtFileForMultiplePaymentsMutation,
} from "../../../generated/graphql";
import MoneyInput from "../../../views/MoneyInput/MoneyInput";

type OpenPaymentType = GetUnpaidScheduledPaymentsQuery["unpaidScheduledPayments"]["items"][number];

type CreateDummyCamtFileProps = {
    openPayments?: Array<OpenPaymentType>;
    setModalOpen: (open: boolean) => void;
};

const CreateDummyCamtFile: FC<CreateDummyCamtFileProps> = ({
    openPayments,
    setModalOpen,
}) => {
    const [selectedPayments, setSelectedPayments] = useState<
        Array<CamtScheduledPaymentInput>
    >();

    useEffect(() => {
        console.log("selectedPayments", selectedPayments);
    }, [selectedPayments]);

    const openPaymentsWithEsr: Array<OpenPaymentType> =
        openPayments?.filter(
            (payment) => payment?.paymentMethod?.esr === true,
        ) ?? [];

    // ----------------------------------------------------------------------------------------------

    const [generate] = useCreateDummyCamtFileForMultiplePaymentsMutation();

    const generateDummyCamtFile = async () => {
        try {
            if (!selectedPayments) return;

            const result = await generate({
                variables: {
                    createDummySwissCamtByScheduledPaymentsInput: {
                        scheduledPayments: selectedPayments,
                    },
                },
            });

            const camtFile =
                result.data?.createDummyCamtFileForMultiplePayments;

            if (camtFile) {
                downLoadXmlFile({camtFile});
            }
            console.log("result", result);
        } catch (error) {
            console.log("error", error);
        } finally {
            setModalOpen(false);
        }
    };

    // ----------------------------------------------------------------------------------------------

    const downLoadXmlFile = ({camtFile}: {camtFile?: string}) => {
        if (!camtFile) {
            return;
        }

        const blob = new Blob([camtFile], {
            type: "application/xml; charset=utf-8",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");

        const today = moment().format("YYYY-MM-DD_HHmm");

        const fileName = `camt.054_DummyFile_${today}.xml`;

        link.href = url;
        link.setAttribute("download", `${fileName}-${today}.xml`);

        // Append to html link element page
        document.body.appendChild(link);

        // link.addEventListener("DEBUG::: error", (e) => {
        //     console.error("Error during download:", e);
        // });

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);
    };

    const AlertHint = () => {
        return (
            <Alert
                type="warning"
                closable
                message="Achtung! Diese Funktion ist ausschließlich zum Testen der swissQResr Funktion. Es wird eine Datei erstellt, die normalerweise von der Bank kommt. Es werden alle Offenen Posten so behandelt als ob diese tatsächlich bezahlt wurde. (Diese Funktion ist nur für Superuser sichtbar.)"
            />
        );
    };

    const renderInputField = ({record}: {record: OpenPaymentType}) => {
        const selectedPayment = selectedPayments?.find(
            (payment) => payment.scheduledPaymentId === record.id,
        );

        const isSelected = Boolean(selectedPayment);

        return (
            <MoneyInput
                disabled={!isSelected}
                value={selectedPayment?.amount}
                onChange={(val) => {
                    const newSelectedPayments = selectedPayments?.map(
                        (payment) => {
                            if (
                                payment.scheduledPaymentId === record.id &&
                                val
                            ) {
                                return {
                                    ...payment,
                                    amount: val,
                                };
                            }

                            return payment;
                        },
                    );

                    setSelectedPayments(newSelectedPayments);
                }}
            />
        );
    };

    const renderOpenPaymentTable = () => {
        const columns: Array<ColumnProps<OpenPaymentType>> = [
            {
                dataIndex: "id",
                title: "id",
            },
            {
                dataIndex: "bookingNumber",
                title: "bookingNumber",
                render: (text, record) =>
                    `${record.booking.bookingNumberPrefix}-${record.booking.bookingNumber}`,
            },
            {
                dataIndex: "paymentAmount",
                title: "paymentAmount",
            },
            {
                dataIndex: "openAmount",
                title: "openAmount",
            },
            {
                dataIndex: "amountToPay",
                title: "amountToPay",
                render: (text, record) => ({
                    displayName: "amountToPay",
                    children: renderInputField({record}),
                }),
            },
        ];

        const rowSelection = {
            type: "checkbox" as const,
            onChange: (
                selectedRowKeys: Array<React.Key>,
                selectedRows: Array<OpenPaymentType>,
            ) => {
                const updatedSelectedPayments = selectedRows.map(
                    (row: OpenPaymentType) => ({
                        scheduledPaymentId: row.id,
                        // amount: row.openAmount ?? row.paymentAmount,
                        // amount of selectedPayment
                        amount:
                            selectedPayments?.find(
                                (payment) =>
                                    payment.scheduledPaymentId === row.id,
                            )?.amount ??
                            row.openAmount ??
                            row.paymentAmount,
                    }),
                );

                setSelectedPayments(updatedSelectedPayments);
            },
            selectedRowKeys: selectedPayments?.map(
                (payment) => payment.scheduledPaymentId,
            ),
        };

        return (
            <Table<OpenPaymentType>
                columns={columns}
                dataSource={openPaymentsWithEsr}
                rowKey={(record) => record.id}
                rowSelection={rowSelection}
                pagination={false}
                size="small"
            />
        );
    };

    const selectionIsMade = selectedPayments && selectedPayments.length > 0;

    return (
        <div>
            <AlertHint />
            {renderOpenPaymentTable()}
            <Button
                type="primary"
                block
                disabled={!selectionIsMade}
                onClick={async () => generateDummyCamtFile()}
            >
                {selectionIsMade
                    ? `Dummy-Datei für ${selectedPayments.length} Posten generieren`
                    : "Keine Posten gewählt"}
            </Button>
        </div>
    );
};

export default memo(CreateDummyCamtFile);
